/**
 * Mixpanel Destination
 * Sends events to Mixpanel HTTP API
 */

import { BaseDestination, DestinationConfig } from './base'
import { EventData } from '../hangarx'

export interface MixpanelConfig extends DestinationConfig {
  token: string
  endpoint?: string
}

export class MixpanelDestination extends BaseDestination {
  name = 'mixpanel'
  private token: string
  private endpoint: string
  
  constructor(config: MixpanelConfig, debug: boolean = false) {
    super(config, debug)
    this.token = config.token
    this.endpoint = config.endpoint || 'https://api.mixpanel.com'
  }
  
  async send(events: EventData[]): Promise<void> {
    if (!this.enabled) {
      this.log('Destination disabled, skipping')
      return
    }
    
    try {
      const mixpanelEvents = events.map(event => this.transformEvent(event))
      
      const response = await fetch(`${this.endpoint}/track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(mixpanelEvents)
      })
      
      if (!response.ok) {
        throw new Error(`Mixpanel HTTP ${response.status}: ${response.statusText}`)
      }
      
      this.log(`Successfully sent ${events.length} events`)
    } catch (error) {
      this.logError('Failed to send events', error)
      throw error
    }
  }
  
  async identify(userId: string, traits?: Record<string, any>): Promise<void> {
    try {
      const payload = {
        $token: this.token,
        $distinct_id: userId,
        $set: traits || {}
      }
      
      const response = await fetch(`${this.endpoint}/engage`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify([payload])
      })
      
      if (!response.ok) {
        throw new Error(`Mixpanel HTTP ${response.status}: ${response.statusText}`)
      }
      
      this.log('User identified', { userId })
    } catch (error) {
      this.logError('Failed to identify user', error)
      throw error
    }
  }
  
  async page(name?: string, properties?: Record<string, any>): Promise<void> {
    const event: EventData = {
      event_type: 'Page Viewed',
      properties: {
        page_name: name || document.title,
        page_url: window.location.href,
        page_path: window.location.pathname,
        ...properties
      }
    }
    
    await this.send([event])
  }
  
  private transformEvent(event: EventData): any {
    return {
      event: event.event_type,
      properties: {
        token: this.token,
        distinct_id: event.user_id || event.anonymous_id,
        $insert_id: this.generateInsertId(),
        time: Date.now(),
        ...event.properties,
        // Add context as properties
        ...(event.context || {})
      }
    }
  }
  
  private generateInsertId(): string {
    return `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }
}
